import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import SearchIcon from '../Images/search-icon.svg';
import FilterIcon from '../Images/filter-icon.svg';
import ImavenOnlineStatus from '../components/Helpers/imaven-online-status';
import { onUserConnected, onUserDisconnected, onUserListReceived, sendRequestUserList } from '../socket';
import UserListIcon from '../Images/userlist-icon.svg';
import InfoIcon from '../Images/info-outline.svg';
import ConversationIcon from '../Images/chat-conversation.svg';
import { AiAgentAvatar, isAiAgentId } from './atoms/ai-agent-avatar';
import HelpButton from './Helpers/HelpButton';
import HelpDialog from './Helpers/HelpDialog';
import { OnlineTag } from './molecules/online-tag';
import UserListItem from './organisms/user-list-item';

const PopupWindow = styled.div`
    width: 200px;
    height: auto;
    min-height: 100px;
    position: fixed;
    top: ${(props) => props.top}px;
    left: ${(props) => props.left}px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    z-index: 10000;
    background: linear-gradient(145deg, #ffffff 0%, #f8faff 100%);
    box-shadow: 0 5px 15px rgba(39, 68, 131, 0.105);
    border: 1px solid #e0e8f5;
    overflow: hidden;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  align-items: center;
  background: rgba(91, 97, 234, 0.08);
  border-radius: 10px;
  padding: 10px 14px;
  flex: 1;
  min-width: 0; 
  height: 42px;
  border: 1px solid rgba(91, 97, 234, 0.15);
  transition: all 0.2s ease;
  
  &:focus-within {
    background: rgba(91, 97, 234, 0.12);
    border-color: rgba(91, 97, 234, 0.3);
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(2473%) hue-rotate(225deg) brightness(102%) contrast(91%);
    margin-right: 10px;
    transition: all 0.2s ease;
  }
  
  &:focus-within img {
    filter: brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(2473%) hue-rotate(225deg) brightness(102%) contrast(91%);
  }
  
  @media (max-width: 768px) {
    height: 40px;
    padding: 8px 12px;
    border-radius: 8px;
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  min-width: 0;
  font-size: 14px;
  font-weight: 400;
  color: #192947;
  background: transparent;

  ::placeholder {
    color: rgba(88, 116, 190, 0.5);
    font-weight: 400;
  }
`;

const FilterWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  margin-left: 10px;
  flex: 0 0 auto;
  
  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) saturate(100%) invert(44%) sepia(31%) saturate(334%) hue-rotate(179deg) brightness(93%) contrast(86%);
    opacity: 0.7;
  }
  
  &:hover img {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const PopupWindowItem = styled.div`
    padding: 12px 16px;
    display: flex;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    color: ${({ disabled }) => (disabled ? '#8f9db3' : '#192947')};
    width: 100%;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(51, 92, 244, 0.15);
    }
`;

const UserListContainer = styled.div`
  height: 100%;
  margin: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
  
  @media (max-width: 768px) {
    margin: 0;
    height: 100%;
    max-height: none;
  }
`;

const HeaderContainer = styled.div`
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid #e0e8f5;
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const PageTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(88, 116, 190, 0.8);
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  img {
    width: 22px;
    height: 22px;
    filter: brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(2473%) hue-rotate(225deg) brightness(102%) contrast(91%);
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  color: #192947;
  font-size: 20px;
  font-weight: 600;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif;
  letter-spacing: -0.3px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SearchSection = styled.div`
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e0e8f5;
  position: sticky;
  top: 65px;
  z-index: 999;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 10px 16px;
  }
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  flex-wrap: nowrap;
  gap: 10px;
`;

const ScrollableContent = styled.div`
  padding: 8px 0 20px 0;
  overflow-y: auto;
  flex: 1;
  /* Slightly tinted so the white conversation cards stand apart */
  background-color: #f5f8fd;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  @media (max-width: 768px) {
    padding: 4px 0;
    padding-bottom: calc(80px + env(safe-area-inset-bottom));
  }
`;


const FilterPopup = styled.div`
  position: absolute;
  width: 250px;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  border: 1px solid #e0e8f5;
  border-radius: 10px;
  box-shadow: 0px 0px 15px rgba(39, 68, 131, 0.14);
  z-index: 10000;
  right: -16px;
  top: 35px;
  padding: 12px;
  
  @media (max-width: 768px) {
    right: -10px;
    width: 200px;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    right: -5px;
    width: 180px;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 15px;
`;

const FilterTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #192947;
  margin: 0 0 10px 0;
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
`;

const FilterCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 1px solid ${props => props.checked ? '#335cf4' : '#cad5e9'};
  border-radius: 3px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.checked ? '#335cf4' : 'transparent'};
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: #33445e;
`;

const FilterRadioOption = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
`;

const FilterRadio = styled.div`
  width: 16px;
  height: 16px;
  border: 1px solid #cad5e9;
  border-radius: 50%;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:after {
    content: '';
    display: ${props => props.checked ? 'block' : 'none'};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #335cf4;
    position: absolute;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
`;

const FilterButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  border: none;
  background-color: ${props => props.primary ? '#335cf4' : '#ffffff'};
  color: white;
  
  &:hover {
    background-color: ${props => props.primary ? '#3a62c4' : '#f8faff'};
  }
`;

const FilterStatusBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #335cf4;
  border: 2px solid #e0e8f5;
  display: ${props => props.show ? 'block' : 'none'};
`;

const UserInfoPopup = styled.div`
  position: fixed;
  width: 380px;
  max-height: 500px;
  background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0px 0px 15px 0px rgba(39, 68, 131, 0.14);
  z-index: 100001;
  border: 1px solid #e0e8f5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 90%;
    max-width: 320px;
    max-height: 80vh;
    left: 50% !important;
    transform: translateX(-50%);
  }
`;

const PopupHeader = styled.div`
  padding: 15px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-bottom: 1px solid #e0e8f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PopupTitle = styled.h3`
  margin: 0;
  color: #192947;
  font-size: 16px;
  font-weight: 500;
`;

const CloseButton = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: #455776;
  
  &:hover {
    background-color: #e6ecf6;
    color: #192947;
  }
`;

const PopupContent = styled.div`
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(25, 41, 71, 0.025);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #e6ecf6;
    border-radius: 6px;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 0.95em;
  }
`;

const UserList = ({ conversationsInit, clientId, onConversationSelect, paramThreadId = '' }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const [conversations, setConversations] = useState(conversationsInit);
  const [filter, setFilter] = useState('');
  const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [FilteredList, setFilterdList] = useState([])
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [threadId, setThreadId] = useState(paramThreadId);
  const [isFilterPopupVisible, setIsFilterPopupVisible] = useState(false);
  const [filterSettings, setFilterSettings] = useState({
    showOnlineOnly: false,
    activityPeriod: 'all',
  });
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const [isInfoPopupVisible, setIsInfoPopupVisible] = useState(false);
  const [infoPopupPosition, setInfoPopupPosition] = useState({ top: 0, left: 0 });
  const [isConversationHistoryVisible, setIsConversationHistoryVisible] = useState(false);
  const [conversationHistoryPosition, setConversationHistoryPosition] = useState({ top: 0, left: 0 });
  const [selectedConversationDetails, setSelectedConversationDetails] = useState(null);
  const [selectedExpertDetails, setSelectedExpertDetails] = useState(null);
  const [isHelpDialogVisible, setIsHelpDialogVisible] = useState(false);

  useEffect(() => {
    setConversations(conversationsInit);
  }, [conversationsInit]);

  // Sort conversations by date, with newest first
  useEffect(() => {
    const sortedConversations = [...conversations].sort((a, b) => {
      const dateA = new Date(a.conversationStartTime || 0); // Fallback to 0 if undefined
      const dateB = new Date(b.conversationStartTime || 0); // Fallback to 0 if undefined
      return dateB - dateA; // Sort in descending order (newest first)
    });
    
    console.log('Sorting conversations by date (newest first):', 
      sortedConversations.map(c => ({
        name: c.name,
        date: new Date(c.conversationStartTime || 0).toLocaleString()
      }))
    );
    
    setFilterdList(sortedConversations);
  }, [conversations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const popupElement = document.querySelector('.popup-menu');
      const dotsElements = document.querySelectorAll('.dots-menu');
      const filterPopupElement = document.querySelector('.filter-popup');
      const filterIconElement = document.querySelector('.filter-icon');
      const infoPopupElement = document.querySelector('.info-popup');
      const conversationHistoryElement = document.querySelector('.conversation-history-popup');

      // Check if click is outside both popup and dots
      const isOutsidePopup = popupElement && !popupElement.contains(event.target);
      const isOutsideDots = Array.from(dotsElements).every(dots => !dots.contains(event.target));
      const isOutsideFilterPopup = filterPopupElement && !filterPopupElement.contains(event.target);
      const isOutsideFilterIcon = filterIconElement && !filterIconElement.contains(event.target);
      const isOutsideInfoPopup = infoPopupElement && !infoPopupElement.contains(event.target);
      const isOutsideConversationHistory = conversationHistoryElement && !conversationHistoryElement.contains(event.target);

      if (isPopupVisible && isOutsidePopup && isOutsideDots) {
        setIsPopupVisible(false);
      }

      if (isFilterPopupVisible && isOutsideFilterPopup && isOutsideFilterIcon) {
        // Apply current filters when closing the popup by clicking outside
        setIsFilterPopupVisible(false);
        applyFilters();
      }
      
      if (isInfoPopupVisible && isOutsideInfoPopup) {
        setIsInfoPopupVisible(false);
      }
      
      if (isConversationHistoryVisible && isOutsideConversationHistory) {
        setIsConversationHistoryVisible(false);
      }
    };

    if (isPopupVisible || isFilterPopupVisible || isInfoPopupVisible || isConversationHistoryVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [conversations, isPopupVisible, isFilterPopupVisible, isInfoPopupVisible, isConversationHistoryVisible, filter, filterSettings]);

  useEffect(() => {
    const cleanupUserConnected = onUserConnected((data) => {
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.expertId === data.userId) {
            return { ...conv, isOnline: true };
          }
          return conv;
        })
      );
    });

    const cleanupUserDisconnected = onUserDisconnected((data) => {
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.expertId === data.userId) {
            return { ...conv, isOnline: false };
          }
          return conv;
        })
      );
    });

    return () => {
      cleanupUserConnected();
      cleanupUserDisconnected();
    };
  }, []);

  // Emit 'request_user_list' to ask for the current user list after attaching listeners
  useEffect(() => {
    sendRequestUserList();
    const cleanupUserListReceived = onUserListReceived((data) => {
      console.log('Connected Users:', data);
      setConversations((prevConversations) =>
        prevConversations.map((conv) => {
          const isOnline = data.some((exp) => conv.expertId === exp.userId);
          return { ...conv, isOnline };
        })
      );
    });
    console.log('Emitted request_user_list');

    return () => {
      cleanupUserListReceived(); // Ensures listener is removed on unmount
    };
  }, [conversationsInit]); // Empty dependency array

  useEffect(() => {
    if (paramThreadId != '' && conversations.length > 0) {
      const convo = conversations.find(x => x.threadId == paramThreadId);
      if (convo) {
        handleConversationClick(paramThreadId, convo.expertId);
      }
    }
  }, [paramThreadId, conversations]);

  const handleConversationClick = async (threadId, expertId) => {
    try {
      setThreadId(threadId);
      if (isAuthenticated && user) {
        const selectedConversation = conversations.find(conv => conv.threadId === threadId);
        const response = await authenticatedAxios.get(`/api/conversations/${expertId}/${threadId}`);
        
        // Make sure we preserve the selectedConversation properties including isOnline status
        const conversationData = {
          ...response.data,
          name: selectedConversation?.name || 'Unnamed Conversation',
          conversationStartTime: selectedConversation?.conversationStartTime || new Date().toISOString(),
          isOnline: selectedConversation?.isOnline || false
        };
        
        onConversationSelect(conversationData, expertId);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const filterUserList = (searchTextEvent) => {
    const text = searchTextEvent.target.value;
    setFilter(text);
    
    // Apply filter immediately when searching
    setTimeout(() => {
      applyFilters();
    }, 100);
  }

  const applyFilters = () => {
    console.log('Applying filters with:', { filter, filterSettings });
    
    // Determine if any filters are active
    const hasActiveFilters = 
      (filter && filter.length > 0) || 
      filterSettings.showOnlineOnly || 
      filterSettings.activityPeriod !== 'all';
    
    // Update the filter active state
    setAreFiltersActive(hasActiveFilters);
    
    let filteredUsers = [...conversations];
    console.log('Starting with users:', filteredUsers.length);

    // Apply text search filter
    if (filter && filter.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.name && user.name.toLowerCase().includes(filter.toLowerCase())
      );
      console.log('After text filter:', filteredUsers.length);
    }

    // Apply online status filter
    if (filterSettings.showOnlineOnly) {
      filteredUsers = filteredUsers.filter(user => user.isOnline);
      console.log('After online filter:', filteredUsers.length);
    }

    // Apply activity period filter
    if (filterSettings.activityPeriod !== 'all') {
      const now = new Date();
      
      switch(filterSettings.activityPeriod) {
        case 'today':
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStartTime) return false;
            const conversationDate = new Date(user.conversationStartTime);
            return conversationDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(now.getDate() - 7);
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStartTime) return false;
            const conversationDate = new Date(user.conversationStartTime);
            return conversationDate >= oneWeekAgo;
          });
          break;
        case 'month':
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(now.getMonth() - 1);
          filteredUsers = filteredUsers.filter(user => {
            if (!user.conversationStartTime) return false;
            const conversationDate = new Date(user.conversationStartTime);
            return conversationDate >= oneMonthAgo;
          });
          break;
        default:
          break;
      }
      console.log('After date filter:', filteredUsers.length);
    }
    
    // Always sort filtered results by newest first
    filteredUsers.sort((a, b) => {
      const dateAFiltered = new Date(a.conversationStartTime || 0); // Fallback to 0 if undefined
      const dateBFiltered = new Date(b.conversationStartTime || 0); // Fallback to 0 if undefined
      return dateBFiltered - dateAFiltered;
    });
    
    // Set the filtered list
    setFilterdList(filteredUsers);
    console.log('Final filtered list:', filteredUsers.length);
  };

  const toggleFilterPopup = (event) => {
    event.stopPropagation();
    setIsFilterPopupVisible(!isFilterPopupVisible);
  }

  const handleFilterChange = (key, value) => {
    // Update the filter settings
    setFilterSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Log for debugging
    console.log(`Changed ${key} to ${value}`);
  }
  
  const resetFilters = () => {
    const defaultSettings = {
      showOnlineOnly: false,
      activityPeriod: 'all',
    };
    setFilterSettings(defaultSettings);
    setFilter('');
    setAreFiltersActive(false);
    setIsFilterPopupVisible(false);
    
    // Reset to show all conversations, but still sorted by newest first
    const sortedConversations = [...conversations].sort((a, b) => {
      const dateA = new Date(a.conversationStartTime || 0);
      const dateB = new Date(b.conversationStartTime || 0);
      return dateB - dateA; // newest first
    });
    setFilterdList(sortedConversations);
  }

  const applyFilterSettings = () => {
    // Calculate if there are any active filters
    const hasActiveFilters = 
      (filter && filter.length > 0) || 
      filterSettings.showOnlineOnly || 
      filterSettings.activityPeriod !== 'all';
    
    // Update the filter active state
    setAreFiltersActive(hasActiveFilters);
    
    // Close popup first
    setIsFilterPopupVisible(false);
    
    // Use setTimeout to ensure state is updated before applying filters
    setTimeout(() => {
      applyFilters();
    }, 0);
  }

  const OpenMoreOptions = (event, threadId, expertId) => {
    setIsPopupVisible(true);
    event.stopPropagation();
    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    // Different positioning for mobile vs desktop
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // For mobile: position the popup centered horizontally and above the dots
      const viewportWidth = window.innerWidth;
      setPopupPosition({
        top: buttonRect.top - 120, // Position above the dots
        left: Math.max(10, (viewportWidth - 200) / 2) // Center horizontally with 10px minimum margin
      });
    } else {
      // For desktop: position to the left of the dots (since dots are on the right edge)
      setPopupPosition({
        top: buttonRect.top,
        left: buttonRect.left - 210 // 200px width + 10px margin
      });
    }
    
    // Save the current conversation details for the popup actions
    const currentConversation = conversations.find(conv => conv.threadId === threadId);
    setSelectedExpertDetails(currentConversation);
  }

  // Add utility function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // View user info quick action
  const viewUserInfoQuick = async (event, threadId, expertId) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!threadId || !expertId) {
      return;
    }

    // Position the info popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = viewportWidth <= 768 ? Math.min(320, viewportWidth * 0.9) : 380;
    const popupHeight = 450;
    
    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);
    
    setInfoPopupPosition({ top, left });
    
    try {
      if (isAuthenticated && user) {
        const response = await authenticatedAxios.get(`/api/conversations/${expertId}/${threadId}`);
        
        // Find expert details
        const expertDetails = conversations.find(conv => conv.threadId === threadId);
        
        // Merge the expert details from the conversation list with the additional data from the API
        const mergedExpertDetails = {
          ...expertDetails,
          email: response.data.expert?.email || "Not available"
        };
        
        setSelectedExpertDetails(mergedExpertDetails);
        setSelectedConversationDetails(response.data);
        setIsInfoPopupVisible(true);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  // View conversation history quick action
  const viewConversationQuick = async (event, threadId, expertId) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!threadId || !expertId) {
      return;
    }

    // Position the conversation history popup centered on screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = viewportWidth <= 768 ? Math.min(320, viewportWidth * 0.9) : 450;
    const popupHeight = 600;
    
    const left = Math.max(20, (viewportWidth - popupWidth) / 2);
    const top = Math.max(20, (viewportHeight - popupHeight) / 2);
    
    setConversationHistoryPosition({ top, left });
    
    try {
      if (isAuthenticated && user) {
        const response = await authenticatedAxios.get(`/api/conversations/${expertId}/${threadId}`);
        
        // Get expert details and merge with the API response data
        const expertDetails = conversations.find(conv => conv.threadId === threadId);
        
        // Merge the expert details from the conversation list with the additional data from the API
        const mergedExpertDetails = {
          ...expertDetails,
          email: response.data.expert?.email || "Not available"
        };
        
        setSelectedExpertDetails(mergedExpertDetails);
        setSelectedConversationDetails(response.data);
        setIsConversationHistoryVisible(true);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  // Function to start conversation directly from info popup
  const startConversationFromInfo = () => {
    setIsInfoPopupVisible(false);
    setIsConversationHistoryVisible(false);
    if (selectedExpertDetails) {
      handleConversationClick(selectedExpertDetails.threadId, selectedExpertDetails.expertId);
    }
  };
  
  // Function to group messages by date for conversation history
  const groupMessagesByDate = (messages) => {
    if (!messages || !messages.length) return {};
    
    return messages.reduce((groups, message) => {
      const date = new Date(message.time).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };
  
  // Function to export conversation history
  const exportConversationHistory = () => {
    if (!selectedConversationDetails || !selectedConversationDetails.messages) return;
    
    try {
      // Format the conversation for export
      const expertName = selectedExpertDetails?.name || "Expert";
      let conversationText = `Conversation with ${expertName}\n`;
      conversationText += `Started: ${new Date(selectedConversationDetails.conversationStartTime || selectedExpertDetails.conversationStartTime).toLocaleString()}\n\n`;
      
      // Group messages by date
      const groupedMessages = groupMessagesByDate(selectedConversationDetails.messages);
      
      Object.entries(groupedMessages).forEach(([date, messages]) => {
        conversationText += `--- ${new Date(date).toLocaleDateString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        })} ---\n\n`;
        
        messages.forEach(message => {
          const isSelf = message?.type === 'user' || message?.senderId === user?.sub;
          const sender = isSelf ? 'You' : (message?.type === 'system' ? 'System' : expertName);
          const time = new Date(message.time).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
          });
          
          conversationText += `[${time}] ${sender}:\n${stripHtml(message.content)}\n\n`;
        });
      });
      
      // Create and download the text file
      const blob = new Blob([conversationText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conversation_with_${expertName.replace(/\s+/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting conversation:', error);
    }
  };

  // Add a new useEffect to handle filter changes
  useEffect(() => {
    // When filter settings change, apply the filters
    applyFilters();
    
    // Log for debugging
    console.log('Filter settings changed:', filterSettings);
  }, [filterSettings]);

  const renderLst = FilteredList.map((conversation, index) => (
    <UserListItem
      key={index}
      name={conversation.name}
      img={conversation.img}
      conversationStartTime={conversation.conversationStartTime}
      isOnline={conversation.isOnline}
      isSelected={conversation.threadId === threadId}
      threadId={conversation.threadId}
      expertId={conversation.expertId}
      onClick={() => handleConversationClick(conversation.threadId, conversation.expertId)}
      onMoreOptionsClick={OpenMoreOptions}
    />
  ));

  return (
    <UserListContainer>
      <HeaderContainer>
        <PageTitleSection>
          <PageIcon>
            <img src={UserListIcon} alt="Users" />
          </PageIcon>
          <HeaderTitle>User List</HeaderTitle>
        </PageTitleSection>
        <HelpButton onClick={() => setIsHelpDialogVisible(true)} />
      </HeaderContainer>
      <SearchSection>
        <SearchRow>
          <SearchBarWrapper>
            <img
              src={SearchIcon}
              alt="Search Icon"
            />
            <SearchInput
              type="text"
              placeholder="Search in user list..."
              value={filter}
              onChange={filterUserList}
            />
          </SearchBarWrapper>
          <FilterWrapper onClick={toggleFilterPopup} className="filter-icon">
            <img src={FilterIcon} alt="Filter" />
            <FilterStatusBadge show={areFiltersActive} />
            
            {isFilterPopupVisible && (
              <FilterPopup className="filter-popup">
                <FilterSection>
                  <FilterTitle>Status</FilterTitle>
                  <FilterOption onClick={() => handleFilterChange('showOnlineOnly', !filterSettings.showOnlineOnly)}>
                    <FilterCheckbox checked={filterSettings.showOnlineOnly}>
                      {filterSettings.showOnlineOnly && (
                        <span style={{ color: '#ffffff', fontSize: '12px' }}>✓</span>
                      )}
                    </FilterCheckbox>
                    <FilterLabel>Show only online advisers</FilterLabel>
                  </FilterOption>
                </FilterSection>
                
                <FilterSection>
                  <FilterTitle>Activity Period</FilterTitle>
                  <FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'all')}>
                    <FilterRadio checked={filterSettings.activityPeriod === 'all'} />
                    <FilterLabel>All time</FilterLabel>
                  </FilterRadioOption>
                  <FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'today')}>
                    <FilterRadio checked={filterSettings.activityPeriod === 'today'} />
                    <FilterLabel>Today</FilterLabel>
                  </FilterRadioOption>
                  <FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'week')}>
                    <FilterRadio checked={filterSettings.activityPeriod === 'week'} />
                    <FilterLabel>Last 7 days</FilterLabel>
                  </FilterRadioOption>
                  <FilterRadioOption onClick={() => handleFilterChange('activityPeriod', 'month')}>
                    <FilterRadio checked={filterSettings.activityPeriod === 'month'} />
                    <FilterLabel>Last 30 days</FilterLabel>
                  </FilterRadioOption>
                </FilterSection>
                
                <FilterButtons>
                  <FilterButton onClick={resetFilters}>Reset</FilterButton>
                  <FilterButton primary onClick={applyFilterSettings}>Apply</FilterButton>
                </FilterButtons>
              </FilterPopup>
            )}
          </FilterWrapper>
        </SearchRow>
      </SearchSection>
      <ScrollableContent>
        {areFiltersActive && (
          <div style={{ 
            padding: '8px 15px', 
            borderBottom: '1px solid #edf1f9',
            fontSize: '13px',
            color: '#455776',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'rgba(51, 92, 244, 0.05)',
            position: 'sticky',
            top: 0,
            zIndex: 990
          }}>
            <div>
              <span style={{ fontWeight: '500' }}>Active Filters:</span> 
              {filterSettings.showOnlineOnly ? '📶 Online only' : ''} 
              {filterSettings.showOnlineOnly && filterSettings.activityPeriod !== 'all' ? ', ' : ''}
              {filterSettings.activityPeriod !== 'all' ? `📅 ${filterSettings.activityPeriod === 'today' ? 'Today' : 
                filterSettings.activityPeriod === 'week' ? 'Last 7 days' : 'Last 30 days'}` : ''}
              {filter ? (filter ? `, 🔍 "${filter}"` : '') : ''}
            </div>
            <div 
              onClick={resetFilters} 
              style={{cursor: 'pointer', fontSize: '12px', color: '#335cf4', fontWeight: '500'}}
            >
              Clear all
            </div>
          </div>
        )}
        {renderLst}
        {isPopupVisible && selectedExpertDetails && (
          <PopupWindow className="popup-menu" top={popupPosition.top} left={popupPosition.left} onClick={(e) => e.stopPropagation()}>
            <PopupWindowItem onClick={(e) => viewUserInfoQuick(e, selectedExpertDetails.threadId, selectedExpertDetails.expertId)}>
              <span style={{ 
                fontSize: "14px", 
                display: "flex", 
                alignItems: "center", 
                gap: "12px",
                fontWeight: "400"
              }}>
                <img src={InfoIcon} alt="Info" style={{ width: "18px", height: "18px", filter: "brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%)" }} />
                Adviser Information
              </span>
            </PopupWindowItem>
            <PopupWindowItem onClick={(e) => viewConversationQuick(e, selectedExpertDetails.threadId, selectedExpertDetails.expertId)}>
              <span style={{ 
                fontSize: "14px", 
                display: "flex", 
                alignItems: "center", 
                gap: "12px",
                fontWeight: "400"
              }}>
                <img src={ConversationIcon} alt="Conversation" style={{ width: "18px", height: "18px", filter: "brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%)" }} />
                Conversation History
              </span>
            </PopupWindowItem>
            <PopupWindowItem onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsPopupVisible(false);
              window.location.href = `/setMetting/${selectedExpertDetails.expertId}/${selectedExpertDetails.threadId}?lang=en`;
            }}>
              <span style={{ 
                fontSize: "14px", 
                display: "flex", 
                alignItems: "center", 
                gap: "12px",
                fontWeight: "400"
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#192947" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><circle cx="9" cy="7" r="0.5" fill="#192947"/><circle cx="15" cy="7" r="0.5" fill="#192947"/></svg>
                Case Analysis
              </span>
            </PopupWindowItem>
          </PopupWindow>
        )}
        
        {/* User Info Popup */}
        {isInfoPopupVisible && selectedExpertDetails && (
          <UserInfoPopup 
            className="info-popup"
            style={{ 
              top: infoPopupPosition.top, 
              left: infoPopupPosition.left
            }}
          >
            <PopupHeader>
              <PopupTitle>Adviser Information</PopupTitle>
              <CloseButton onClick={() => setIsInfoPopupVisible(false)}>✕</CloseButton>
            </PopupHeader>
            <PopupContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                {isAiAgentId(selectedExpertDetails.expertId) ? (
                  <AiAgentAvatar size={64} radius={14} label={`${selectedExpertDetails.name || 'Advisor'} (AI agent)`} />
                ) : (
                <img 
                  src={selectedExpertDetails.img || "https://icons.veryicon.com/png/o/internet--web/prejudice/user-128.png"} 
                  alt={selectedExpertDetails.name || "Expert"}
                  style={{ 
                    width: window.innerWidth <= 768 ? '75px' : '80px', 
                    height: window.innerWidth <= 768 ? '60px' : '65px', 
                    borderRadius: '10px',
                    border: '1px solid #e0e8f5',
                    objectFit: "cover"
                  }}
                />
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: window.innerWidth <= 768 ? '16px' : '18px', fontWeight: '500', color: '#192947', marginBottom: '3px' }}>
                    {selectedExpertDetails.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#455776' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: selectedExpertDetails.isOnline ? '#1f9d6b' : '#cad5e9'
                    }}></div>
                    {selectedExpertDetails.isOnline ? 'Online' : 'Offline'}
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '18px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#192947', fontSize: '14px', fontWeight: '500', 
                           borderBottom: '1px solid #e0e8f5', paddingBottom: '5px' }}>
                  Adviser Details
                </h4>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <div style={{ width: '110px', color: '#65748d', fontSize: '13px' }}>Name</div>
                  <div style={{ color: '#192947', fontSize: '13px', wordBreak: 'break-word', flex: 1 }}>
                    {selectedExpertDetails.name || "Not available"}
                  </div>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <div style={{ width: '110px', color: '#65748d', fontSize: '13px' }}>Email</div>
                  <div style={{ color: '#192947', fontSize: '13px', wordBreak: 'break-word', flex: 1 }}>
                    {selectedExpertDetails.email && selectedExpertDetails.email !== "Not available" ? 
                      <a 
                        href={`mailto:${selectedExpertDetails.email}`} 
                        style={{
                          color: '#335cf4',
                          textDecoration: 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selectedExpertDetails.email}
                      </a>
                      : 
                      "Not available"
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <div style={{ width: '110px', color: '#65748d', fontSize: '13px' }}>Status</div>
                  <div style={{ color: '#192947', fontSize: '13px', wordBreak: 'break-word', flex: 1 }}>
                    {selectedExpertDetails.isOnline ? "Online" : "Offline"}
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '18px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#192947', fontSize: '14px', fontWeight: '500', 
                           borderBottom: '1px solid #e0e8f5', paddingBottom: '5px' }}>
                  Conversation Details
                </h4>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <div style={{ width: '110px', color: '#65748d', fontSize: '13px' }}>Started</div>
                  <div style={{ color: '#192947', fontSize: '13px', wordBreak: 'break-word', flex: 1 }}>
                    {selectedExpertDetails.conversationStartTime ? 
                      new Date(selectedExpertDetails.conversationStartTime).toLocaleString() : 
                      "Not available"}
                  </div>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <div style={{ width: '110px', color: '#65748d', fontSize: '13px' }}>Message Count</div>
                  <div style={{ color: '#192947', fontSize: '13px', wordBreak: 'break-word', flex: 1 }}>
                    {selectedConversationDetails?.messages?.length || 0}
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <button 
                  onClick={() => {
                    setIsInfoPopupVisible(false);
                    viewConversationQuick(new Event('click'), selectedExpertDetails.threadId, selectedExpertDetails.expertId);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: '#335cf4',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <img src={ConversationIcon} alt="Messages" style={{ width: '14px', height: '14px', filter: 'brightness(0) saturate(100%) invert(9%) sepia(17%) saturate(6773%) hue-rotate(204deg) brightness(91%) contrast(87%)' }} />
                  View Messages
                </button>
              </div>
            </PopupContent>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              padding: '15px',
              borderTop: '1px solid #e0e8f5',
              gap: '10px'
            }}>
              <button 
                onClick={() => setIsInfoPopupVisible(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: '#ffffff',
                  color: '#192947',
                  transition: 'all 0.2s'
                }}
              >
                Close
              </button>
              <button 
                onClick={startConversationFromInfo}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: '#335cf4',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
              >
                Open Conversation
              </button>
            </div>
          </UserInfoPopup>
        )}
        
        {/* Conversation History Popup */}
        {isConversationHistoryVisible && selectedExpertDetails && selectedConversationDetails && (
          <UserInfoPopup 
            className="conversation-history-popup"
            style={{ 
              top: conversationHistoryPosition.top, 
              left: conversationHistoryPosition.left,
              width: window.innerWidth <= 768 ? '90%' : '450px',
              maxWidth: window.innerWidth <= 768 ? '320px' : '450px',
              maxHeight: window.innerWidth <= 768 ? '80vh' : '600px'
            }}
          >
            <PopupHeader>
              <PopupTitle>Conversation History with {selectedExpertDetails.name || "Adviser"}</PopupTitle>
              <CloseButton onClick={() => setIsConversationHistoryVisible(false)}>✕</CloseButton>
            </PopupHeader>
            <PopupContent>
              {selectedConversationDetails.messages && selectedConversationDetails.messages.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button 
                      onClick={exportConversationHistory}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: '#1f9d6b',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      Export as Text
                    </button>
                  </div>
                  
                  {Object.entries(groupMessagesByDate(selectedConversationDetails.messages)).map(([date, messages]) => (
                    <div key={date}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        margin: '15px 0',
                      }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e6ecf6' }}></div>
                        <span style={{ 
                          padding: '0 10px', 
                          fontSize: '12px', 
                          color: '#7b8aa3'
                        }}>
                          {new Date(date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#e6ecf6' }}></div>
                      </div>
                      
                      {messages.map((message, index) => {
                        const isSelf = message?.type === 'user' || message?.senderId === user?.sub;
                        const isSystem = message?.type === 'system';
                        
                        if (isSystem) {
                          return (
                            <div 
                              key={index}
                              style={{ 
                                borderRadius: '8px',
                                padding: '10px 12px',
                                marginBottom: '10px',
                                backgroundColor: 'rgba(239, 155, 97, 0.1)',
                                borderLeft: '3px solid #ef9b61'
                              }}
                            >
                              <div style={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '5px'
                              }}>
                                <div style={{ 
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  color: '#a2611c'
                                }}>
                                  System Message
                                </div>
                                <div style={{ 
                                  fontSize: '11px',
                                  color: '#7b8aa3'
                                }}>
                                  {new Date(message.time).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </div>
                              <div style={{ 
                                fontSize: '13px',
                                color: '#33445e',
                                whiteSpace: 'pre-wrap',
                                lineHeight: '1.4'
                              }}>
                                {stripHtml(message.content)}
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div 
                            key={index}
                            style={{ 
                              borderRadius: '8px',
                              padding: '10px 12px',
                              marginBottom: '10px',
                              backgroundColor: isSelf ? 'rgba(51, 92, 244, 0.15)' : '#f5f8fd',
                              borderLeft: `3px solid ${isSelf ? '#335cf4' : '#e0e8f5'}`
                            }}
                          >
                            <div style={{ 
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '5px'
                            }}>
                              <div style={{ 
                                fontSize: '12px',
                                fontWeight: '500',
                                color: isSelf ? '#335cf4' : '#192947'
                              }}>
                                {isSelf ? 'You' : (selectedExpertDetails.name || 'Adviser')}
                              </div>
                              <div style={{ 
                                fontSize: '11px',
                                color: '#7b8aa3'
                              }}>
                                {new Date(message.time).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                            <div style={{ 
                              fontSize: '13px',
                              color: '#33445e',
                              whiteSpace: 'pre-wrap',
                              lineHeight: '1.4'
                            }}>
                              {stripHtml(message.content)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#7b8aa3', padding: '20px 0' }}>
                  No messages in this conversation yet.
                </div>
              )}
            </PopupContent>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              padding: '15px',
              borderTop: '1px solid #e0e8f5',
              gap: '10px'
            }}>
              <button 
                onClick={() => setIsConversationHistoryVisible(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: '#ffffff',
                  color: '#192947',
                  transition: 'all 0.2s'
                }}
              >
                Close
              </button>
              <button 
                onClick={startConversationFromInfo}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: '#335cf4',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
              >
                Open Conversation
              </button>
            </div>
          </UserInfoPopup>
        )}
      </ScrollableContent>
      
      {/* Add Help Dialog at the end of the component */}
      {isHelpDialogVisible && (
        <HelpDialog 
          title="User List Help Guide" 
          helpType="userList" 
          onClose={() => setIsHelpDialogVisible(false)} 
        />
      )}
    </UserListContainer>
  );
};

export default UserList;